import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { Gauge, ChartBar, FilePdf, IdentificationCard, Users, SignOut, Key } from "@phosphor-icons/react";

const NAV = [
  { to: "/sales", label: "Overview", icon: Gauge, end: true },
  { to: "/sales/comp", label: "My Comp", icon: ChartBar },
  { to: "/sales/proposals", label: "Proposals", icon: FilePdf },
  { to: "/sales/packets", label: "Assignment Packets", icon: IdentificationCard },
  { to: "/sales/workers", label: "Workers", icon: Users },
];

// Tightly scoped portal for sales reps: top bar + tabs, no admin sidebar.
// Child pages read the rep profile (incl. review_required) via useOutletContext().
export default function SalesLayout() {
  const { user, logout } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/sales/me").then((r) => setRep(r.data || null)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-950 text-white border-b-4 border-emerald-500" data-testid="sales-header">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4 flex items-center gap-3">
          <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-10 w-10 rounded object-contain bg-white/5 p-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-black leading-tight truncate">Safety Onsite <span className="text-emerald-400">· Sales</span></div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 truncate" data-testid="sales-user-email">{user.email}</div>
          </div>
          <button onClick={() => setShowPw(true)} data-testid="sales-change-password-btn" title="Change password"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2.5 py-1.5 rounded hover:bg-slate-800">
            <Key size={14} /> <span className="hidden sm:inline">Password</span>
          </button>
          <button onClick={logout} data-testid="sales-logout-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 hover:text-white px-2.5 py-1.5 rounded bg-slate-800 hover:bg-slate-700">
            <SignOut size={14} /> Sign out
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-4 md:px-8 mt-3 flex flex-wrap gap-x-1" data-testid="sales-nav">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`sales-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap border-b-[3px] -mb-[4px] transition-colors ${
                isActive ? "text-white border-emerald-400" : "text-slate-400 border-transparent hover:text-white"}`}>
              <Icon size={16} weight="duotone" /> {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <Outlet context={{ rep, autoSend: rep ? rep.review_required === false : false }} />
      </main>
      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
    </div>
  );
}
